import { useEffect, useState } from 'react'
import { Icon } from '@iconify/react'
import { put } from '../../utils/Api'
import { useGlobalContext } from '../../utils/Context'
import FormLoader from '../loader/FormLoader'
import type { Tag } from './TagChips'
import './tags.scss'

type Props = {
  /** booking | floating | cancelled — which collection the shipment lives in. */
  kind: 'booking' | 'floating' | 'cancelled'
  shipmentId: string
  /** Tag ids currently on the shipment. */
  value?: string[]
  /** Called with the saved ids so the parent can update its copy. */
  onChange?: (tagIds: string[]) => void
}

/**
 * Apply and remove tags on one shipment. Open to every admin role — the agent
 * handling a parcel is usually the one who spots it needs flagging. Creating
 * the tags themselves is restricted to super admin and customer service, which
 * is why there is no "new tag" affordance here.
 *
 * Saves the whole set on each toggle rather than add/remove deltas, so two
 * admins with the same shipment open cannot half-apply each other's changes.
 */
function TagPicker({ kind, shipmentId, value, onChange }: Props) {
  const { tags, tagsLoading, notify } = useGlobalContext()
  const [saving, setSaving] = useState(false)
  const [selected, setSelected] = useState<string[]>(value || [])

  // The parent usually fetches the shipment after this mounts, so `value`
  // arrives on a later render — seeding state once would leave the picker
  // permanently empty. Compare by content, not identity, or a fresh array on
  // every parent render would fight the local optimistic update.
  const valueKey = (value || []).join(',')
  useEffect(() => {
    setSelected(value || [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueKey, shipmentId])

  const activeTags: Tag[] = (tags as Tag[] | undefined ?? []).filter((tag) => tag.isActive)

  // A deleted tag already on this shipment stays selectable-off but is not
  // offered for adding — it is shown so the admin can see and remove it.
  const inactiveOnShipment: Tag[] = (tags as Tag[] | undefined ?? []).filter(
    (tag) => !tag.isActive && selected.includes(tag._id),
  )

  const save = (next: string[]) => {
    const previous = selected
    setSelected(next)
    setSaving(true)
    put(`admin/shipment/${kind}/${shipmentId}/tags`, { tags: next })
      .then((result) => {
        if (result.status === 200) {
          onChange?.(next)
        } else {
          // Put the chips back where they were — a failed save must not look
          // like it worked.
          setSelected(previous)
          notify('error', result.message || 'Could not update tags')
        }
        setSaving(false)
      })
      .catch(() => {
        setSelected(previous)
        notify('error', 'Could not update tags')
        setSaving(false)
      })
  }

  const toggle = (tagId: string) => {
    if (saving) return
    save(
      selected.includes(tagId)
        ? selected.filter((id) => id !== tagId)
        : [...selected, tagId],
    )
  }

  if (tagsLoading) return <FormLoader position='left' />

  if (!activeTags.length && !inactiveOnShipment.length) {
    return (
      <span className='tag-chips-empty'>
        No tags created yet — add them under Shipment Tags.
      </span>
    )
  }

  return (
    <div className='tag-picker'>
      {[...activeTags, ...inactiveOnShipment].map((tag) => {
        const isOn = selected.includes(tag._id)
        return (
          <button
            key={tag._id}
            type='button'
            className='tag-option'
            disabled={saving}
            onClick={() => toggle(tag._id)}
            title={tag.isActive ? tag.name : `${tag.name} (deleted tag)`}
            style={{
              background: isOn ? `${tag.colour}1A` : 'transparent',
              color: isOn ? tag.colour : '#7c7c7c',
              border: `1px solid ${isOn ? `${tag.colour}55` : '#dcdcdc'}`,
              opacity: tag.isActive ? undefined : 0.55,
            }}
          >
            <Icon icon={isOn ? 'mdi:check' : 'mdi:plus'} />
            {tag.name}
          </button>
        )
      })}
    </div>
  )
}

export default TagPicker
